import { fetchUpcomingReminders, type UpcomingReminderRow } from "./api";

// Re-fetch upcoming reminders periodically so newly-created events and
// edits made in other tabs eventually flow in. Five minutes is a fine
// trade-off for a self-hosted single-user app.
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

// setTimeout takes a 32-bit signed int — anything past ~24.8 days
// overflows and fires immediately. Skip work farther out than that
// and let the next refetch pick it up.
const MAX_TIMER_DELAY_MS = (2 ** 31 - 1) - 1000;

type FiredSet = Set<string>;

interface Scheduled {
  timers: Map<string, ReturnType<typeof setTimeout>>;
  fired: FiredSet;
  refetchHandle: ReturnType<typeof setInterval> | null;
  visibilityHandler: (() => void) | null;
  refetchInFlight: AbortController | null;
}

let state: Scheduled | null = null;

function makeKey(eventId: string, triggerMs: number): string {
  // Include the trigger timestamp so editing the event (new triggerMs)
  // produces a new key and the edited reminder can fire again.
  return `${eventId}@${triggerMs}`;
}

function computeTriggerMs(r: UpcomingReminderRow, now: Date): number {
  // Wall-clock semantics: construct a Date in the user's local TZ from
  // the raw {year, month, day, hour, minute}. The server returns the
  // event's local fields without any TZ conversion.
  const eventStart = new Date(
    r.startYear,
    r.startMonth - 1,
    r.startDay,
    r.startHour ?? 0,
    r.startMinute ?? 0,
    0,
    0,
  );
  void now;
  return eventStart.getTime() - r.reminderMinutesBefore * 60_000;
}

function formatBody(r: UpcomingReminderRow): string {
  const time =
    r.startHour != null && r.startMinute != null
      ? new Date(0, 0, 0, r.startHour, r.startMinute).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "all-day";
  return `${time} · ${r.calendarName}`;
}

function fire(r: UpcomingReminderRow): void {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(r.heading, {
      body: formatBody(r),
      tag: `reminder:${r.id}`,
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      window.dispatchEvent(
        new CustomEvent("calendar:navigate-to", {
          detail: { year: r.startYear, month: r.startMonth, day: r.startDay },
        }),
      );
      n.close();
    };
  } catch {
    // Some browsers throw if invoked from a non-user-gesture context.
  }
}

function clearTimers(s: Scheduled): void {
  for (const t of s.timers.values()) clearTimeout(t);
  s.timers.clear();
}

async function refetchAndSchedule(s: Scheduled): Promise<void> {
  if (s.refetchInFlight) s.refetchInFlight.abort();
  const controller = new AbortController();
  s.refetchInFlight = controller;
  let rows: UpcomingReminderRow[];
  try {
    rows = await fetchUpcomingReminders(undefined, controller.signal);
  } catch (err) {
    if ((err as Error)?.name === "AbortError") return;
    return; // network errors stay quiet; next interval will retry
  } finally {
    if (s.refetchInFlight === controller) s.refetchInFlight = null;
  }

  clearTimers(s);
  const now = Date.now();
  const today = new Date();
  for (const r of rows) {
    const triggerMs = computeTriggerMs(r, today);
    const delay = triggerMs - now;
    if (delay <= 0) continue; // already past
    if (delay > MAX_TIMER_DELAY_MS) continue;
    const key = makeKey(r.id, triggerMs);
    if (s.fired.has(key)) continue;
    const handle = setTimeout(() => {
      s.fired.add(key);
      s.timers.delete(key);
      fire(r);
    }, delay);
    s.timers.set(key, handle);
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export async function startReminderScheduler(): Promise<void> {
  if (state) return;
  if (typeof Notification === "undefined") return;

  const fired: FiredSet = new Set();
  const s: Scheduled = {
    timers: new Map(),
    fired,
    refetchHandle: null,
    visibilityHandler: null,
    refetchInFlight: null,
  };
  state = s;

  await refetchAndSchedule(s);
  s.refetchHandle = setInterval(() => {
    void refetchAndSchedule(s);
  }, REFETCH_INTERVAL_MS);

  // Refresh when the tab comes back to the foreground; suspended tabs
  // can drift their timer clocks, and we may have missed an interval.
  s.visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      void refetchAndSchedule(s);
    }
  };
  document.addEventListener("visibilitychange", s.visibilityHandler);
}

export function stopReminderScheduler(): void {
  if (!state) return;
  clearTimers(state);
  if (state.refetchHandle) clearInterval(state.refetchHandle);
  if (state.visibilityHandler) {
    document.removeEventListener("visibilitychange", state.visibilityHandler);
  }
  state.refetchInFlight?.abort();
  state = null;
}

// Exposed for the EventForm / EventModal flow: nudge the scheduler to
// re-pull after a user-initiated edit so reminders don't wait for the
// next polling tick.
export function refreshReminders(): void {
  if (!state) return;
  void refetchAndSchedule(state);
}
